import {
    panel,
    PropertiesPanelContent,
    PropertiesPanelHint,
    PropertiesPanelPropertyConfig,
    PropertiesPanelSwatch
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import React from 'react';
import { T } from 'src/shared/helpers';
import { createTripleNumberConfig } from './propertyConfigs';
import { describeMirroredSpace, liveTransform, loadedTransform } from './transformSpace';

// UNA MESH VERA HA DEI VERTICI, un Empty è un'origine e basta. La regola vive qui e non in due
// posti: la legge l'albero per scegliere il glifo della riga, e la legge il pannello per sapere
// se questo soggetto può avere un materiale suo. Il nome e i metadata sono il ripiego per quando
// il modello di Babylon non è a portata.
export const isRealMesh = (node: { data?: any; metadata?: { [key: string]: any } }) => {
    const mesh = node.data;
    if (mesh && typeof mesh.getTotalVertices === 'function') return mesh.getTotalVertices() > 0;
    if (mesh && typeof mesh.getClassName === 'function') return mesh.getClassName() !== 'TransformNode';
    return !node.metadata?.isTransformNode && !node.metadata?.isRootNode;
};

// GLI ASSI CHE NON SI TOCCANO SI PRENDONO DAL MOTORE. Un campo cambia UN numero, ma la terna si
// scrive intera: gli altri due vanno letti da qualche parte, e leggerli dal pannello voleva dire
// riportarli dov’erano all’ultima lettura. Il gizmo sposta l’oggetto senza dirlo a nessuno —
// il motore non emette alcun evento sulle trasformate — quindi dopo un trascinamento il pannello
// ha ancora i numeri di prima: battere Y rimetteva X e Z dov’erano, e l’oggetto scattava indietro.
const baseOf = (node: ObjectNode, property: 'position' | 'rotation' | 'scaling'): [number, number, number] =>
    node.data ? liveTransform(node.data)[property] : node[property];

export type ObjectConfigHelpers = {
    sceneEditor: any;
    updateProperty: (updates: any) => void;
    onPropertyChanged?: (property: string, value: any) => void;
    isVTOEnabledInScene: boolean;
    /** L'AR della scena è accesa: senza, la card della singola mesh non ha di che parlare. */
    isAREnabledInScene: boolean;
    fineTuningSettings?: any;
    handleVirtualTryonFinetuning: () => void;
    handleResetFinetuningSettings: () => void;
    /** Porta la colonna di sinistra sui materiali e accende quello scelto qui. */
    revealMaterial?: (materialId: string) => void;
    /** La catena della scala: accesa, i tre assi si muovono insieme. */
    scaleLinked: boolean;
    setScaleLinked: (linked: boolean) => void;
    /** Crea un materiale nuovo e lo assegna qui. Assente dove il motore non sa crearne. */
    createMaterial?: () => void;
};

export type ObjectNode = {
    id: string;
    name: string;
    position: [number, number, number];
    rotation: [number, number, number];
    scaling: [number, number, number];
    /** Bloccata: le trasformate si leggono e non si toccano. Vedi `sceneLocks`. */
    locked?: boolean;
    materialOptions: { value: string; label: string; previewUrl?: string }[];
    selectedMaterial: { value: string; label: string };
    showInAR: boolean;
    enableInVTO: boolean;
    metadata?: { [key: string]: any };
    data?: any;
};

const createPropertyConfig = <T extends PropertiesPanelContent>(config: T) => config;

// Helper per formattare numeri molto piccoli o molto grandi
const formatNumberValue = (value: number): string => {
    if (Math.abs(value) < 0.0001) {
        return value.toExponential(3);
    } else if (Math.abs(value) < 0.01) {
        return value.toFixed(6);
    } else {
        return value.toFixed(3);
    }
};

// Configurazione per il gruppo delle proprietà di base
const createBasicPropertiesGroup = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig => {
    // DIRE PERCHÉ, QUANDO IL PANNELLO NON PUÒ FARCI NIENTE. Sotto il nodo che porta un modello
    // importato nel sistema di coordinate della scena le rotazioni si vedono rovesciate: il
    // numero è giusto, il verso no. Finché quello specchio resta dov'è — e resta, perché i
    // prodotti di anni ci sono appoggiati sopra — la sola cosa onesta è ammetterlo qui, accanto
    // ai numeri che ne risentono.
    const mirrored = node.data ? describeMirroredSpace(node.data) : null;

    return createPropertyConfig({
        type: 'group',
        // «BASIC PROPERTIES» NON DICE DI CHE COSA. Il gruppo tiene posizione, rotazione e scala:
        // si chiama Transform, come lo chiama chiunque lavori in 3D e come lo chiama il mockup.
        id: 'transform',
        header: T._('Transform', 'Admin'),
        hint: mirrored ? <PropertiesPanelHint text={mirrored} /> : undefined,
        defaultIsOpen: true,
        properties: [
            // IL NOME NON STA QUI. Sta nella testata dell'Inspector, dove si legge e si
            // modifica: tenerlo anche fra le proprietà voleva dire due campi per la stessa
            // cosa, uno sopra l'altro, e quello sotto sembrava un altro nome.

            // Position
            createTripleNumberConfig(
                'position',
                T._('Position', 'Admin'),
                node.position,
                (axis, value) => {
                    const newPosition = [...baseOf(node, 'position')] as [number, number, number];
                    const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
                    newPosition[axisIndex] = value;
                    helpers.updateProperty({ position: newPosition });
                },
                undefined,
                node.locked
            ),

            // Rotation
            createTripleNumberConfig(
                'rotation',
                T._('Rotation', 'Admin'),
                node.rotation,
                (axis, value) => {
                    const newRotation = [...baseOf(node, 'rotation')] as [number, number, number];
                    const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
                    newRotation[axisIndex] = value;
                    helpers.updateProperty({ rotation: newRotation });
                },
                '°',
                node.locked
            ),

            // Scaling
            // LA CATENA VALE MENTRE SI SCRIVE, non dopo: accesa, il numero che si batte su un asse
            // trascina anche gli altri due. Ma li trascina **in proporzione**, non li pareggia: su un
            // oggetto 1 · 2 · 1, battere 3 su X dà 3 · 6 · 3 e non 3 · 3 · 3. È il gesto con cui si
            // ingrandisce una cosa senza deformarla, e pareggiare i tre numeri è precisamente
            // deformarla — un oggetto lungo il doppio diventerebbe un cubo.
            //
            // UN ASSE A ZERO NON HA UN RAPPORTO da mantenere: qualunque cosa lo moltiplichi resta
            // zero. Lì la catena può solo copiare il valore, altrimenti la cosa resterebbe schiacciata
            // per sempre e il campo non risponderebbe.
            {
                ...createTripleNumberConfig('scaling', T._('Scale', 'Admin'), node.scaling, (axis, value) => {
                    const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
                    const current = baseOf(node, 'scaling');

                    if (helpers.scaleLinked) {
                        const previous = current[axisIndex];
                        const scaling = (previous ? current.map((size) => size * (value / previous)) : [value, value, value]) as [number, number, number];

                        helpers.updateProperty({ scaling });
                        return;
                    }

                    const newScaling = [...current] as [number, number, number];
                    newScaling[axisIndex] = value;
                    helpers.updateProperty({ scaling: newScaling });
                }),
                link: {
                    active: helpers.scaleLinked,
                    onToggle: helpers.setScaleLinked,
                    title: T._('Keep the three axes together', 'Admin')
                }
            },

            // RIPORTARE È UNA COSA SOLA su nove numeri: sta sotto la terna, non sopra, perché la si
            // sceglie dopo aver visto che cosa si sta per buttare via.
            //
            // NON ALL'IDENTITÀ, MA A COM'ERA AL CARICAMENTO (utente, 2026-09-16): sotto un modello
            // importato 0,0,0 non è «a posto», è un altro posto — quello del modellatore lo aveva
            // scelto lui, e l'identità lo cancellava senza ritorno. Vedi `transformSpace`.
            createPropertyConfig({
                type: 'action',
                id: 'reset-transform',
                label: T._('Reset transform', 'Admin'),
                onClick: () => helpers.updateProperty(loadedTransform(node.data))
            })
        ]
    });
};

// Configurazione per il gruppo dei materiali
// I MATERIALI CHE STANNO SOTTO UN EMPTY. Un Empty non ha una superficie — è una trasformazione —
// ma è il posto da cui si vede, di un pezzo intero del modello, di che cosa è fatto. Si guardano
// le mesh discendenti, ognuna col suo materiale, e ogni materiale una volta sola: due mesh con lo
// stesso materiale sono UN materiale usato due volte.
const inheritedMaterials = (node: ObjectNode): { value: string; label: string; previewUrl?: string }[] => {
    const descendants: any[] = typeof node.data?.getChildMeshes === 'function' ? node.data.getChildMeshes(false) : [];
    const seen = new Set<string>();

    return descendants
        .map((mesh) => mesh?.material)
        .filter((material) => {
            if (!material?.id || seen.has(material.id)) return false;
            seen.add(material.id);
            return true;
        })
        .map((material) => {
            const known = node.materialOptions?.find((option) => option.value === material.id);
            return { value: material.id, label: known?.label ?? material.name ?? material.id, previewUrl: known?.previewUrl };
        });
};

// UN EMPTY NON HA UN MATERIALE SUO, e fingere che ce l'abbia era il difetto: lo slot mostrava il
// PRIMO materiale della scena — perché il nodo, non avendone uno, si prendeva il primo
// dell'elenco — cioè diceva una cosa falsa, e cambiandolo si sarebbe assegnato un materiale a una
// cosa che non può portarne. Ora sotto un Empty ci sono i materiali dei suoi, in sola lettura: si
// vedono e da lì si arriva al loro posto nella libreria. E se sotto non c'è niente di dipinto la
// card non si scrive affatto — uno slot vuoto qui direbbe «assegnamene uno», che è proprio la
// cosa che un Empty non può fare (mockup, § pannello di una mesh).
const createMaterialsGroup = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig | null => {
    if (!isRealMesh(node)) {
        const inherited = inheritedMaterials(node);
        if (!inherited.length) return null;

        return createPropertyConfig({
            type: 'group',
            id: 'materials',
            header: T._('Materials', 'Admin'),
            defaultIsOpen: true,
            properties: inherited.map((material) =>
                createPropertyConfig({
                    type: 'resource',
                    id: `material-slot-${material.value}`,
                    preview: <PropertiesPanelSwatch $url={material.previewUrl} $tint={panel.kindAsset} $size={26} />,
                    name: material.label,
                    emptyLabel: T._('Empty slot', 'Admin'),
                    // NIENTE «sostituisci» e niente «svuota»: questo materiale non è di questo nodo,
                    // è di una mesh più sotto. Cambiarlo da qui vorrebbe dire ridipingere tutte le
                    // mesh che lo portano — un comando che oggi non esiste da nessuna parte.
                    onReveal: helpers.revealMaterial ? () => helpers.revealMaterial?.(material.value) : undefined
                })
            )
        });
    }

    // La faccia del materiale di adesso sta nell'elenco: è la stessa fotografia, letta una volta
    // sola per tutta la scena.
    const currentMaterial = node.materialOptions?.find((option) => option.value === node.selectedMaterial?.value);

    return createPropertyConfig({
        type: 'group',
        id: 'materials',
        header: T._('Materials', 'Admin'),
        defaultIsOpen: true,
        properties: [
            // IL MATERIALE DI UNA MESH È UN ASSET ASSEGNATO, non una voce di tendina. Una tendina
            // mostra dei nomi e chiede di fidarsi: qui si vede la sfera del materiale, il suo nome, e
            // i due gesti che servono — sostituiscilo, e portami dov'è nella sua lista. È lo stesso
            // slot dell'ambiente del World e dello slot di una texture: cambia l'asset dentro, non il
            // posto.
            //
            // L'ELENCO È QUELLO DI PRIMA, con gli stessi materiali e lo stesso `onChange`: cambia
            // dove si apre, non che cosa contiene.
            createPropertyConfig({
                type: 'resource',
                id: 'material-slot',
                // IL CERCHIO VUOTO È IL POSTO, non un materiale senza nome: la pallina si colora solo
                // dove un materiale c'è davvero, ed è la stessa marca che quel materiale porta nella
                // propria lista.
                preview: (
                    <PropertiesPanelSwatch $url={currentMaterial?.previewUrl} $tint={node.selectedMaterial?.value ? panel.kindAsset : undefined} $size={26} />
                ),
                name: node.selectedMaterial?.label,
                // «No material» DICE COM'È, non che cosa farne. Uno slot vuoto è un posto che aspetta:
                // il nome dice che è vuoto, la riga sotto dice il gesto che lo riempie.
                emptyLabel: T._('Empty slot', 'Admin'),
                emptyCaption: T._('Choose a material', 'Admin'),
                options: node.materialOptions,
                value: node.selectedMaterial?.value,
                searchPlaceholder: T._('Search materials...', 'Admin'),
                optionTint: panel.kindAsset,
                // IL COMANDO C'È SOLO SE C'È DAVVERO: una voce che crea un materiale su un motore che
                // non sa crearne è una promessa che si scopre rotta al clic.
                onCreate: helpers.createMaterial ? { label: T._('New material', 'Admin'), onClick: helpers.createMaterial } : undefined,
                onSelect: (value) => {
                    const chosen = node.materialOptions.find((option) => option.value === value);
                    if (chosen) helpers.updateProperty({ selectedMaterial: chosen });
                },
                // LE DUE LISTE SI SEGUONO: da qui il materiale si ritrova dov'è, senza cercarlo a
                // mano in un elenco che nel frattempo è cambiato sezione.
                onReveal:
                    helpers.revealMaterial && node.selectedMaterial?.value ? () => helpers.revealMaterial?.(String(node.selectedMaterial.value)) : undefined,
                // SVUOTARE È TOGLIERE L'ASSEGNAZIONE, non cancellare il materiale: quello resta nella
                // sua libreria e su tutte le altre mesh che lo portano. Qui si stacca da questa.
                onClear: node.selectedMaterial?.value ? () => helpers.updateProperty({ selectedMaterial: { value: '', label: '' } }) : undefined
            })
        ]
    });
};

// CON L'AR DELLA SCENA SPENTA LA CARD RESTA, SPENTA (utente, 2026-08-27: «voglio as is, che le card
// si disabilitino e non che spariscano»). Farla sparire era la strada breve, e nasconde una cosa
// che vale la pena vedere: che questa mesh HA un'impostazione AR, e che a tenerla ferma è una
// scelta presa altrove. Un capitolo che sparisce non si può cercare, e chi non lo trova non sa se
// non esiste o se è spento.
//
// L'AR generale sta nelle impostazioni della scena, Behaviours › Augmented Reality — ed è quello
// che dice la didascalia, con le parole del vecchio backoffice.
const createARGroup = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'group',
        id: 'ar-settings',
        header: T._('AR Settings', 'Admin'),
        defaultIsOpen: false,
        properties: [
            createPropertyConfig({
                type: 'switch',
                id: 'show-in-ar',
                label: T._('Show in AR', 'Admin'),
                checked: helpers.isAREnabledInScene && node.showInAR,
                disabled: !helpers.isAREnabledInScene,
                caption: helpers.isAREnabledInScene ? undefined : T._('AR not enabled. Please go Settings and enable the feature.', 'Admin'),
                onChange: (checked) => {
                    // IL MOTORE LEGGE `metadata.AR`, e qui si scriveva `metadata.showInAR`: una chiave
                    // che non guarda nessuno. L'interruttore si muoveva e l'esportazione AR continuava
                    // a portarsi dietro quello che si era appena tolto. I metodi del motore scrivono la
                    // chiave giusta e scendono nei figli — togliere un pezzo toglie ciò che ci sta sotto.
                    if (checked) helpers.sceneEditor?.enableARForMesh?.(node.data);
                    else helpers.sceneEditor?.disableARForMesh?.(node.data);

                    helpers.updateProperty({ showInAR: checked });
                }
            })
        ]
    });
};

// E il VTO si comporta come l'AR qui sopra: card sempre presente, interruttore spento quando il VTO
// della scena è spento. Era la forma del vecchio backoffice, ed è quella che si tiene.
const createVTOGroup = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'group',
        id: 'vto-settings',
        header: T._('VTO Settings', 'Admin'),
        defaultIsOpen: false,
        properties: [
            createPropertyConfig({
                type: 'switch',
                id: 'enable-in-vto',
                label: T._('Enable in VTO', 'Admin'),
                checked: node.enableInVTO && helpers.isVTOEnabledInScene,
                disabled: !helpers.isVTOEnabledInScene,
                caption: helpers.isVTOEnabledInScene ? undefined : T._('VTO not enabled. Please go Settings and enable the feature.', 'Admin'),
                onChange: (checked) => {
                    // Stessa storia dell'AR: il motore legge `metadata.TryOn` per decidere che cosa
                    // mostrare nel try-on, e qui si scriveva `metadata.enableInVTO`. Sono i metodi del
                    // motore a scrivere la chiave giusta, sul nodo e su tutta la sua discendenza.
                    if (checked) helpers.sceneEditor?.enableTryOnForMesh?.(node.data);
                    else helpers.sceneEditor?.disableTryOnForMesh?.(node.data);

                    helpers.updateProperty({ enableInVTO: checked });
                }
            })
        ]
    });
};

// IL FINETUNING VALE SU UNA MESH SOLA, E LA SCEGLIE IL MOTORE — non noi, e non chi guarda. Il
// try-on esporta il modello di UN nodo e su QUEL nodo scrive il risultato, in
// `metadata.tryOnFineTuningSettings`; a rileggerlo, più tardi, torna a chiedere al motore quale
// sia. Se il finetuning si facesse altrove, l'impostazione finirebbe su una mesh che nessuno
// interroga: un lavoro fatto e buttato, senza che niente lo dica.
//
// Il nodo è quello che risponde a `getTryOnMeshVisible()`: visibile, SENZA GENITORE, e con
// almeno un figlio abilitato al try-on. È lo stesso che usa la procedura guidata del VTO per
// leggere e scrivere il finetuning — quindi qui non si sceglie un criterio, si chiede il suo.
//
// «Ha figli» — la regola del vecchio backoffice — era più larga: in una gerarchia come le nostre
// i nodi con figli sono parecchi, e il finetuning compariva su tutti mentre a contare era uno.
export const isTryOnFineTuningNode = (node: { data?: any }, sceneEditor: any): boolean => {
    const tryOnMesh = sceneEditor?.getTryOnMeshVisible?.() ?? null;
    if (!tryOnMesh || !node.data) return false;

    return tryOnMesh === node.data || tryOnMesh.id === node.data.id;
};

// E le altre tre condizioni sono quelle di sempre: VTO acceso per la scena, impostazioni VTO
// esistenti, e un provider scelto. Senza provider il finetuning non sa nemmeno che cornice aprire.
const createFineTuningGroup = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelPropertyConfig | null => {
    const isTryOnMesh = isTryOnFineTuningNode(node, helpers.sceneEditor);
    const hasTryOnSettings = !!helpers.sceneEditor?.getTryOnSettings?.();
    const hasProvider = !!helpers.sceneEditor?.selectedProvider;

    if (!helpers.isVTOEnabledInScene || !isTryOnMesh || !hasTryOnSettings || !hasProvider) {
        return null;
    }

    const properties = [];

    // C'È GIÀ UN FINETUNING SU QUESTA MESH, e vale la pena dirlo: senza, l'unico modo di sapere se
    // il lavoro di prima è stato salvato sarebbe rifarlo. La condizione era `isRootNode` — un
    // metadato nostro — mentre la card ormai esiste solo sul nodo che il motore riconosce come suo:
    // due criteri per la stessa cosa, e il giorno in cui non coincidessero la riga sparirebbe senza
    // motivo.
    if (helpers.fineTuningSettings) {
        properties.push(
            createPropertyConfig({
                type: 'text',
                id: 'finetuning-info',
                label: T._('Status', 'Admin'),
                value: `✅ ${T._('Finetuning settings loaded', 'Admin')}`,
                disabled: true
            })
        );
    }

    properties.push(
        createPropertyConfig({
            type: 'icon',
            id: 'virtual-tryon-finetuning',
            label: T._('Virtual Try-on Finetuning', 'Admin'),
            icon: <span>🎯</span>,
            onIconClick: helpers.handleVirtualTryonFinetuning
        }),
        createPropertyConfig({
            type: 'icon',
            id: 'reset-finetuning',
            label: T._('Reset Finetuning Settings', 'Admin'),
            icon: <span>🔄</span>,
            // Niente da azzerare, niente da premere: come nel vecchio backoffice.
            disabled: !helpers.sceneEditor?.getMeshFineTuningData?.(node.data),
            onIconClick: helpers.handleResetFinetuningSettings
        })
    );

    return createPropertyConfig({
        type: 'group',
        id: 'finetuning-settings',
        header: T._('Fine Tuning', 'Admin'),
        defaultIsOpen: false,
        properties
    });
};

// LA COLONNA HA TRE CAPITOLI, come gli altri pannelli e come il mockup: dove sta la mesh
// (Scene), di che cosa è fatta (Shading), e le regole che le si danno (Settings).
//
// Cinque card di fila senza capitoli sono un elenco senza indice — e qui le card sono cinque
// perché AR, VTO e Finetuning sono tre impostazioni diverse della stessa mesh.
const objectPropertiesByType = (node: ObjectNode, helpers: ObjectConfigHelpers): PropertiesPanelContent[] => {
    const settings = [createARGroup(node, helpers), createVTOGroup(node, helpers), createFineTuningGroup(node, helpers)].filter(
        Boolean
    ) as PropertiesPanelPropertyConfig[];

    const sections: PropertiesPanelContent[] = [
        createPropertyConfig({
            type: 'section',
            id: 'object-scene',
            title: T._('Scene', 'Admin'),
            properties: [createBasicPropertiesGroup(node, helpers)]
        })
    ];

    // IL CAPITOLO ESISTE SE HA DI CHE PARLARE: un Empty senza niente di dipinto sotto non ha una
    // sezione «Shading», e un indice con sotto il vuoto è una promessa.
    const materialsGroup = createMaterialsGroup(node, helpers);

    if (materialsGroup) {
        sections.push(
            createPropertyConfig({
                type: 'section',
                id: 'object-shading',
                title: T._('Shading', 'Admin'),
                properties: [materialsGroup]
            })
        );
    }

    if (settings.length) {
        sections.push(
            createPropertyConfig({
                type: 'section',
                id: 'object-settings',
                title: T._('Settings', 'Admin'),
                properties: settings
            })
        );
    }

    return sections;
};

// Hook principale da esportare (simile a useMaterialsPropertiesCreator)
export const useObjectPropertiesCreator = (): ((node: ObjectNode, helpers: ObjectConfigHelpers) => PropertiesPanelContent[]) => {
    return (node: ObjectNode, helpers: ObjectConfigHelpers) => {
        return objectPropertiesByType(node, helpers);
    };
};
